import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import {
  Card,
  CardContent,
  Container,
  Typography,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Stack,
  Alert,
  Link,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Public,
  Shield,
  CreditCard,
  Store,
  Apple,
  Google,
  Close,
} from "@mui/icons-material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CardTopUpDialog from "./CardTopup"; // Import the CardTopUpDialog component
import CardTransactionDetailsDialog from "./CardTransactionDetailsDialog"; // Import the dialog component
import CardBlock from "./CardBlock"; // Import the CardBlock component

const DebitCard = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [cardDetails, setCardDetails] = useState(null);
  const [cardAppStatus, setcardAppStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
  const [blockCardDialogOpen, setBlockCardDialogOpen] = useState(false);
  const [cardBalance, setCardBalance] = useState(0.0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [cardStatus, setCardStatus] = useState(null);
  const [userStatus, setUserStatus] = useState(Cookies.get("userStatus"));

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setDialogOpen(true);
  };

  // get kycstatus from session storage
  const kycStatus = Cookies.get("kycStatus");

  const features = [
    {
      icon: <Public />,
      title: "Globally accepted",
    },
    {
      icon: <Google />,
      title: "Google Pay Compatible",
    },
    {
      icon: <Apple />,
      title: "Apple Pay Compatible",
    },
    {
      icon: <Store />,
      title: "Works everywhere",
    },
    {
      icon: <Shield />,
      title: "Heavily secure",
    },
  ];

  // send email to backend to get card details
  const cardIssueRequest = async () => {
    try {
      const token = Cookies.get("token");
      const email = Cookies.get("email");
      const response = await axios.get("/api/api/card/create/" + email, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === "success") {
        setcardAppStatus("processing"); // Update the card application status
      }
    } catch (error) {
      console.error("Error in card issue request:", error);
    }
  };

  const [cardTrasactions, setCardTransactions] = useState([]);

  // Fetch transactions based on bocard_id
  const fetchTransactions = async (bocardId) => {
    try {
      const token = Cookies.get("token");
      const response = await axios.get(
        `/api/api/card/transactions/${bocardId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      if (response.data.status === "success") {
        // sort transactions by date latest first
        const sortedTransactions = response.data.data.sort(
          (a, b) => new Date(b.date_of_transaction) - new Date(a.date_of_transaction)
        );
        setCardTransactions(sortedTransactions); // Set the transactions if available
      } else {
        console.error("Error fetching transactions:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const fetchBalance = async (bocardId) => {
    try {
      const token = Cookies.get("token");
      const response = await axios.get(`/api/api/card/balance/${bocardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCardBalance(response.data.payload.result.item1.balance); // Set the transactions if available
      } else {
        console.error("Error fetching balance:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        // Obtain email from sessionStorage
        const email = Cookies.get("email");
        if (!email) {
          throw new Error("Email not found in session storage.");
        }

        const bank_url = `${process.env.REACT_APP_BANK_API_URL}/findUser/${email}`;
        const bank_auth_token = `${process.env.REACT_APP_BANK_API_TOKEN}`;

        // Make API call to the bank API
        const token = Cookies.get("token");
        const response = await axios.get(`/api/api/card/${email}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Handle the response
        if (response.data.status === "success") {
          if (response.data.data.application_status === "CARD_ISSUED") {
            setcardAppStatus("issued"); // Card is issued
          } else if (response.data.data.application_status === null) {
            setcardAppStatus("not_initiated");
          } else {
            setcardAppStatus("processing"); // Card is still processing
          }

          setCardDetails(response.data.data); // Set card details if available
          // set session card token id
          Cookies.set("cardTokenId", response.data.data.card_token_id, {
            secure: true,
            sameSite: "Strict",
          });

          if (response.data.data.bocard_id !== null) {
            fetchTransactions(response.data.data.bocard_id);
            fetchBalance(response.data.data.bocard_id);
            setCardStatus(response.data.data.card_status);
          }
        } else {
          setcardAppStatus("not_initiated"); // Card is still processing
        }
      } catch (err) {
        console.error("Error fetching card details:", err);
        setError(true); // Show card issuance screen if there's an error
      } finally {
        setLoading(false);
      }
    };

    fetchCardDetails();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h6">Loading card details...</Typography>
      </Container>
    );
  }

  // Check user status
  if (userStatus !== "active") {
    return (
      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h6" color="error">
          Your account status is: {userStatus}. Please contact support for
          assistance.
        </Typography>
      </Container>
    );
  }

  if (cardAppStatus === "issued") {
    return (
      <Container
        maxWidth="xl"
        sx={{ backgroundColor: "#f5f5f5", px: { xs: 1, sm: 2, md: 3 } }}
      >
        <Card sx={{ borderRadius: 4, boxShadow: 0 }}>
          <CardContent>
            <Typography variant="body1" color="#7517a7" fontWeight="bold">
              My Card
            </Typography>
            <Box
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: 4,
                p: 3,
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
                position: "relative",
                maxWidth: 400,
                mt: 2,
              }}
            >
              {/* Card Header */}
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                X Card
              </Typography>

              {/* Card Number */}
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  letterSpacing: 2,
                  mb: 2,
                }}
              >
                {showDetails
                  ? cardDetails.card_number.replace(/(\d{4})(?=\d)/g, "$1 ")
                  : "**** **** **** ****"}
              </Typography>

              {/* Card Holder and Expiry */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Card Holder
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {cardDetails.first_name} {cardDetails.last_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Expiry Date
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {cardDetails.card_exp_month}/{cardDetails.card_exp_year}
                  </Typography>
                </Box>
              </Box>

              {/* CVV */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="body2" color="#fff">
                    Card Balance
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {showDetails ? cardBalance : "***"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="#fff">
                    CVV
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {showDetails ? cardDetails.card_cvv : "***"}
                  </Typography>
                </Box>
                {/* Eye Icon for Show/Hide */}
                <IconButton
                  onClick={() => setShowDetails(!showDetails)}
                  sx={{ color: "#fff" }}
                >
                  {showDetails ? (
                    <RemoveRedEyeOutlinedIcon />
                  ) : (
                    <VisibilityOffOutlinedIcon />
                  )}
                </IconButton>
              </Box>
            </Box>

            {cardStatus === "ACTIVE" ? (
              <Box display="flex" justifyContent="start" mt={4} gap={2}>
                <Button
                  variant="outlined"
                  endIcon={<AddCircleIcon />}
                  sx={{ width: 170, textTransform: "none" }}
                  onClick={() => setTopUpDialogOpen(true)}
                >
                  Topup Card
                </Button>

                <Button
                  variant="outlined"
                  endIcon={<AcUnitOutlinedIcon />}
                  sx={{ width: 170, textTransform: "none" }}
                  onClick={() => setBlockCardDialogOpen(true)}
                >
                  Freeze Card
                </Button>
              </Box>
            ) : (
              <Box display="flex" justifyContent="start" mt={4} gap={2}>
                <Button
                  variant="outlined"
                  endIcon={<AddCircleIcon />}
                  sx={{ width: 170 }}
                  disabled
                >
                  Topup Card
                </Button>

                <Button
                  variant="outlined"
                  endIcon={<AcUnitOutlinedIcon />}
                  sx={{ width: 170, textTransform: "none" }}
                  onClick={() => setBlockCardDialogOpen(true)}
                >
                  Unfreeze Card
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 4, boxShadow: 0, marginTop: 2 }}>
          <CardContent>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2  // margin bottom for spacing
                      }}>
                        <Typography variant="body1" fontWeight="bold" color="#7517a7">
                          Recent Transactions
                        </Typography>
                        <Link
                          href="/transactions"  // Replace with your transaction page route
                          // component={RouterLink}  // Assuming you're using React Router
                          sx={{
                            color: '#7517a7',
                            textDecoration: 'none',
                            fontSize: '14px',
                            '&:hover': {
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          See more
                        </Link>
                      </Box>

            <Box sx={{ overflowX: "auto", borderRadius: 4 }}>
              {cardTrasactions.length > 0 ? (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    borderRadius: "8px",
                    overflow: "hidden",
                    fontSize: "14px",
                  }}
                >
                  <thead>
                    <tr
                      style={{ backgroundColor: "#f5f5f5", textAlign: "left" }}
                    >
                      <th style={{ padding: "12px", fontWeight: "bold" }}>
                        Date/Time
                      </th>
                      <th style={{ padding: "12px", fontWeight: "bold" }}>
                        UUID
                      </th>
                      <th style={{ padding: "12px", fontWeight: "bold" }}>
                        Transaction Type
                      </th>
                      <th style={{ padding: "12px", fontWeight: "bold" }}>
                        Merchant Name
                      </th>
                      <th style={{ padding: "12px", fontWeight: "bold" }}>
                        Description
                      </th>
                      <th style={{ padding: "12px", fontWeight: "bold" }}>
                        Amount
                      </th>
                      <th style={{ padding: "12px", fontWeight: "bold" }}>
                        Status
                      </th>
                      <th style={{ padding: "12px", fontWeight: "bold" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cardTrasactions.slice(0, 5).map((tx, index) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                                                <td style={{ padding: "12px" }}>
                          {tx.date_of_transaction || "N/A"}
                        </td>
<td style={{ padding: "12px" }}>
  {tx.transaction_id
    ? `${tx.transaction_id.substring(0, 2)}***${tx.transaction_id.slice(-6)}`
    : "N/A"}
</td>
                        <td style={{ padding: "12px" }}>
                          {tx.transaction_type || "N/A"}
                        </td>
                        <td style={{ padding: "12px" }}>
                          {tx.merchant_name || "N/A"}
                        </td>
                        <td style={{ padding: "12px" }}>
  {tx.description
    ? tx.description.length > 15
      ? `${tx.description.substring(0, 5)}....${tx.description.slice(-10)}`
      : tx.description
    : "N/A"}
</td>
                        <td
                          style={{
                            padding: "12px",
                            fontWeight:
                              tx.transaction_mode === "Credit"
                                ? "bold"
                                : "normal",
                          }}
                        >
                          {tx.transaction_mode === "Debit"
                            ? `-$${tx.amount}`
                            : tx.transaction_mode === "Credit"
                            ? `+$${tx.amount}`
                            : "N/A"}
                        </td>
                        <td style={{ padding: "12px" }}>
                          {tx.status === "COMPLETION" ? (
                            <Typography
                              variant="body2"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "green",
                                fontWeight: "bold",
                              }}
                            >
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  backgroundColor: "green",
                                  borderRadius: "50%",
                                  display: "inline-block",
                                  marginRight: 8,
                                }}
                              ></span>
                              Completed
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color:
                                  tx.status === "DECLINED" ? "red" : "orange",
                                fontWeight: "bold",
                              }}
                            >
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  backgroundColor: "green",
                                  borderRadius: "50%",
                                  display: "inline-block",
                                  marginRight: 8,
                                }}
                              ></span>
                              {tx.status
                                ? tx.status.charAt(0).toUpperCase() +
                                  tx.status.slice(1).toLowerCase()
                                : "N/A"}
                            </Typography>
                          )}
                        </td>
                        <td style={{ padding: "12px" }}>
                          <IconButton
                            aria-label="details"
                            size="small"
                            onClick={() => handleTransactionClick(tx)}
                          >
                            <ArrowForwardIosIcon fontSize="inherit" />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 2 }}
                >
                  No transactions found
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
        {/* Dialog for topup card */}
        <CardTopUpDialog
          open={topUpDialogOpen}
          onClose={() => setTopUpDialogOpen(false)}
        />

        {/* Dialog for blocking card */}
        <CardBlock
          open={blockCardDialogOpen}
          onClose={() => setBlockCardDialogOpen(false)}
          bocardId={cardDetails.card_token_id}
          uniqueId={cardDetails.bocard_id}
          cardStatus={cardStatus}
        />

        {/* Dialog Component */}
        <CardTransactionDetailsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          transaction={selectedTransaction}
        />
      </Container>
    );
  }

  if (cardAppStatus === "processing") {
    return (
      <Container
        maxWidth="xl"
        sx={{ backgroundColor: "#f5f5f5", px: { xs: 1, sm: 2, md: 3 } }}
      >
        <Alert
          severity="info"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#d1ecf1",
            color: "#0c5460",
            mx: 3,
            borderRadius: 4,
          }}
        >
          Your card is still processing. Please check back later.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 400, mx: "auto" }}>
      {" "}
      {/* Reduced max-width */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid rgba(210, 204, 212, 0.1)",
          background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
          position: "relative",
          overflow: "visible",
          maxWidth: "100%",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -1,
            left: -1,
            right: -1,
            bottom: -1,
            borderRadius: "inherit",
            padding: "1.5px",
            background:
              "linear-gradient(135deg,rgb(241, 238, 243) 0%,rgb(242, 237, 245) 100%)",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            // borderBottom: '1px solid rgba(117, 23, 167, 0.1)',
            // background: 'linear-gradient(145deg, rgba(117, 23, 167, 0.05) 0%, rgba(92, 17, 132, 0.05) 100%)',
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            fontWeight="600"
            sx={{
              letterSpacing: "-0.5px",
              background: "linear-gradient(135deg, #7517a7 0%, #5c1184 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {/* <CreditCard sx={{ color: '#7517a7' }} /> */}
            Get a prepaid card instantly
          </Typography>
        </Box>

        {/* Features */}
        <Box sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            {features.map((feature, index) => (
              <Box
                key={index}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1,
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  transform:
                    hoveredFeature === index ? "translateX(8px)" : "none",
                  background:
                    hoveredFeature === index ? "#9e94a3" : "transparent",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    p: 0.75,
                    display: "flex",
                    color: "#7517a7",
                    borderRadius: "50%",
                    background: "rgba(117, 23, 167, 0.1)",
                    transition: "all 0.3s ease",
                    transform:
                      hoveredFeature === index ? "scale(1.1)" : "scale(1)",
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "#49546c",
                    transition: "all 0.3s ease",
                    transform:
                      hoveredFeature === index ? "scale(1.02)" : "scale(1)",
                  }}
                >
                  {feature.title}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* Get Started Button */}
          <Box
            sx={{
              mt: 4,
              textAlign: "center",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: 0,
                right: 0,
                height: "1px",
                background:
                  "linear-gradient(90deg, transparent, rgba(117, 23, 167, 0.2), transparent)",
              },
            }}
          >
            <Button
              variant="contained"
              onClick={() => setOpen(true)}
              disabled={kycStatus !== "verified"}
              sx={{
                bgcolor: "#7517a7",
                px: 4,
                py: 1.2,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                position: "relative",
                overflow: "hidden",
                background: "linear-gradient(135deg, #7517a7 0%, #5c1184 100%)",
                transition: "all 0.3s ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, #5c1184 0%, #7517a7 100%)",
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(117, 23, 167, 0.3)",
                  "&::before": {
                    opacity: 0.3,
                  },
                },
                "& .MuiButton-label": {
                  position: "relative",
                  zIndex: 1,
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        </Box>
      </Card>
      {/* Dialog with enhanced styling */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionProps={{
          timeout: 500,
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
          },
        }}
      >
        {/* <IconButton
        onClick={() => setOpen(false)}
        sx={{
          position: 'absolute',
          right: -40,  // Changed from -12 to move it further right
          top: -40,    // Changed from -12 to move it higher
          bgcolor: 'white',
          border: '1px solid rgba(117, 23, 167, 0.2)',
          width: 32,
          height: 32,
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: '#7517a7',
            // border: '1px solid #7517a7',
            transform: 'rotate(90deg)',
            '& .MuiSvgIcon-root': {
              color: '#7517a7',
            }
          },
          zIndex: 9999, // Increased z-index to ensure it stays on top
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Close
          sx={{
            fontSize: 18,
            color: '#7517a7',
            transition: 'all 0.3s ease',
          }}
        />
      </IconButton> */}

        <DialogContent>
          {/* Demo Card */}
          <Box
            sx={{
              mt: 2,
              p: 3,
              borderRadius: 3,
              background:
                "linear-gradient(135deg,rgb(13, 13, 14) 0%,rgb(34, 33, 34) 100%)",
              color: "white",
              minHeight: 150,
              boxShadow: "0 8px 24px rgba(117, 23, 167, 0.2)",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <CreditCard />
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 500 }}>
                Virtual Prepaid Card
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                letterSpacing: 4,
                opacity: 0.9,
              }}
            >
              **** **** **** ****
            </Typography>
          </Box>

          {/* Fee Information */}
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#2c2c2c" }}
            >
              You'll be charged a one-time fee of $5 from your balance.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.5,
                lineHeight: 1.6,
              }}
            >
              {/* You'll be charged a one-time fee of $4 from your USD balance. */}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#7517a7",
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#2c2c2c" }}>
                  I confirm to pay the one-time fee of $5
                </Typography>
              }
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ p: 2.5, pt: 0, display: "flex", justifyContent: "center" }}
        >
          <Button
            variant="contained"
            disabled={!agreed}
            sx={{
              bgcolor: "#7517a7",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              "&:hover": {
                bgcolor: "#5c1184",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(117, 23, 167, 0.5)",
              },
            }}
            onClick={async () => {
              try {
                await cardIssueRequest(); // Call the card issue request function
                setOpen(false); // Close the dialog after the request
              } catch (error) {
                console.error("Error issuing card:", error);
              }
            }}
          >
            Create Virtual Card
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DebitCard;
